import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";
import styles from "../TreeComponents.module.scss";

import { IconButton } from "@components/atoms/buttons/icon-button/icon-button";
import { RootState } from "@src/reducers/store";
import checkBoxIcon from "../../icons/checkbox-icon.svg";
import checkBoxCheckedIcon from "../../icons/checked-checkbox-icon.svg";
import transferredCheckboxIcon from "@src/assets/svg/transferred-checkbox-icon.svg";
import RenderInTreeIcon from "@src/assets/svg/render-in-tree-icon.svg";
/* import checkBoxIconWarning from "@src/assets/svg/checkbox-icon-warning.svg"; */

import { activateMachineStructureDropAreaOverlay } from "../../use-cases/activate-machine-structure-drop-area-overlay";
import { deactivateAllAreaOverlays } from "../../use-cases/deactivate-all-drop-area-overlays";
import { isDraggable } from "../../use-cases/is-draggable";
import { manageUnassignedNodeViewInBlueprintMode } from "../../use-cases/manage-unassigned-node-view-in-blueprint-mode";
import { FilteredIconInfoWrapperOurNode } from "./filtered-icon-info-wrapper-our-node";
import { handleDragStartOurNode } from "./use-cases/handle-drag-start-our-node";
import { getInitialStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-initial-structure-nodeset";
import {
  emptyNodeDataObject,
  updateActiveInitialStructureNode,
  updateActiveMachineStructureNode,
} from "@src/reducers/your-reducer";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import {
  writeEmptyNodeDetails,
  writeNodeDetailsOurNode,
} from "./use-cases/write-node-details-our-node";
import { ChildType } from "@common/types/tree-types";
import { useI18N } from "@src/language/language-context";
import { OurNode } from "@src/cloud-connect/poc/our-node";

type TreeProps = {
  child: ChildType & OurNode;
  updateCheckboxFunc: (node) => void;
  type: "initial" | "machine";
  activeId?: string;
  activateDragAndDrop: boolean;
};

const cssClassName = "filtered-node";

export const FilteredNodeOurNode = ({
  child,
  updateCheckboxFunc,
  type,
  activateDragAndDrop,
}: TreeProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const dispatch = useDispatch();
  const i18n = useI18N();
  const initialStructureNodeset = getInitialStructureNodeset();
  const machineStructureNodeset = getMachineStructureNodeset();

  const activeNodeId = useSelector((state: RootState) =>
    type === "initial"
      ? state.yourSlice.activeInitialStructureNode
      : state.yourSlice.activeMachineStructureNode,
  );

  const texts = {
    structureIconText: i18n("modules.filteredNodeOurNode.structureIconText"),
  };

  const createArrayFromFlatMapForAncestors = (type: "initial" | "machine") => {
    return Array.from(
      type === "initial"
        ? getInitialStructureNodeset().getFlatMap()
        : getMachineStructureNodeset().getFlatMap(),
    );
  };

  const handleWriteNodeDetails = useCallback(
    (child) => {
      writeNodeDetailsOurNode(
        dispatch,
        child,
        createArrayFromFlatMapForAncestors(type),
        type,
      );
    },
    [dispatch, type],
  );

  const handleWriteEmptyNodeDetails = useCallback(
    (child) => {
      writeEmptyNodeDetails(dispatch, child);
    },
    [dispatch],
  );

  const handleClickOnNode = useCallback(
    (type: "initial" | "machine") => {
      if (child.name !== "Root" && type === "machine" && !child.unassigned) {
        handleWriteNodeDetails(child);
        dispatch(updateActiveMachineStructureNode(child.key));
        dispatch(updateActiveInitialStructureNode(""));
      } else if (child.name !== "Root" && type === "initial") {
        handleWriteNodeDetails(child);
        dispatch(updateActiveInitialStructureNode(child.key));
        dispatch(updateActiveMachineStructureNode(""));
      } else {
        handleWriteEmptyNodeDetails(emptyNodeDataObject);
        dispatch(updateActiveMachineStructureNode(""));
        dispatch(updateActiveInitialStructureNode(""));
      }
    },
    [child, handleWriteNodeDetails, dispatch, handleWriteEmptyNodeDetails],
  );

  function navigateToNodeInTreeStructure() {
    handleClickOnNode(type);
    const nodeSet =
      type === "initial" ? initialStructureNodeset : machineStructureNodeset;
    nodeSet.setFilteredFlatMap(new Map());
    nodeSet.setShowFilterPanel(false);
  }

  const returnCheckboxIconByState = (childInfo: boolean) => {
    return childInfo ? checkBoxCheckedIcon : checkBoxIcon;
  };

  const handleCheckBoxClick = useCallback((childInfo, updateCheckboxFunc) => {
    updateCheckboxFunc(childInfo);
  }, []);

  const handleOnDragEnd = () => {
    deactivateAllAreaOverlays();
  };

  const defineActionOnDrag = (e) => {
    handleDragStartOurNode(e, type);
    if (!activateDragAndDrop) {
      activateMachineStructureDropAreaOverlay();
    }
  };

  const renderCheckBoxButton = () => {
    if (child.transfered && type === "initial") {
      return (
        <IconButton
          disabled={true}
          icon={transferredCheckboxIcon}
          fun={() => ({})}
          variant={"no-padding"}
        />
      );
    }

    /* if (
      child.unassigned &&
      type === "machine" &&
      child.node_type !== "FOLDER" &&
      !child.selected
    ) {
      return (
        <IconButton
          icon={checkBoxIconWarning}
          fun={() => handleCheckBoxClick(child, updateCheckboxFunc)}
          variant={"no-padding"}
        />
      );
    } */

    return (
      <IconButton
        icon={returnCheckboxIconByState(child.selected)}
        fun={() => handleCheckBoxClick(child, updateCheckboxFunc)}
        variant={"no-padding"}
      />
    );
  };

  return (
    <li
      className={styles[cssClassName]}
      key={child.key}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDragStart={(e) => defineActionOnDrag(e)}
      draggable={isDraggable(child)}
      onDragEnd={handleOnDragEnd}
    >
      <span
        className={cn(styles[`${cssClassName}__outer-wrapper`], {
          [styles[`${cssClassName}__outer-wrapper--selected`]]:
            activeNodeId === child.key,
        })}
      >
        <span className={styles[`${cssClassName}__checkbox-info-wrapper`]}>
          <span className={styles[`${cssClassName}__checkbox-wrapper`]}>
            {renderCheckBoxButton()}
          </span>
          <FilteredIconInfoWrapperOurNode
            child={child}
            type={type}
            handleClickOnNode={handleClickOnNode}
          />
        </span>
        {manageUnassignedNodeViewInBlueprintMode(child, type)}
        {(isHovered || activeNodeId === child.key) && (
          <IconButton
            tooltip={texts.structureIconText}
            tooltipPosition="left"
            fun={() => navigateToNodeInTreeStructure()}
            icon={RenderInTreeIcon}
          ></IconButton>
        )}
      </span>
    </li>
  );
};
